//@ts-nocheck
import { damageMarkerIcons } from "../shared/images.js";
import { storeDamageMarkers, storeInteriorDamageMarkers } from "../shared/damage-map.js";
import { mkAbs } from "../shared/page-init.js";
import { consoleMsg } from "../shared/utility.js";
const app = window.app;

export class MarkerManager {
	constructor(mapId, type, alertId, removeBtnId = "removeMarkerButton") {
		this.mapId = mapId; // e.g., "myMap" or "myMap11"
		this.type = type; // "exterior" or "interior"
		this.alertId = alertId; // e.g., "alert1" or "alert11"
		this.rect = null;
		this.x = null;
		this.y = null;
		this.removeBtn = document.getElementById(removeBtnId);
	}

	mapClicked(event) {
		event.stopPropagation();
		this.rect = event.target.getBoundingClientRect();
		this.x = event.clientX - this.rect.left;
		this.y = event.clientY - this.rect.top;

		if (!this.rect || !event.target.id.includes("Img")) return;

		this.showDamageDialog();
	}

	showDamageDialog() {
		const dialog = document.getElementById(this.alertId);
		if (!dialog) {
			console.error("Alert not found:", this.alertId);
			return;
		}
		dialog.present();
	}

	addMarkerFromDialog() {
		const selectVal = app.content[this.alertId + "Data"];
		if (!selectVal) return;

		const { src, alt } = this.getMarkerImage(selectVal);
		if (!src) return;

		// 🔧 Snapshot these BEFORE async image load, so resetState() doesn't kill them
		const rect = this.rect;
		const x = this.x;
		const y = this.y;
		if (!rect) return; // safety

		const img = new Image();

		consoleMsg("[MARKER SRC]", {
			input: src,
			resolved: mkAbs(src),
			base: app.settings?.base,
		});

		img.src = mkAbs(src); // normalize to root-absolute

		img.onload = () => {
			consoleMsg("[MARKER LOADED]", img.src, img.naturalWidth, img.naturalHeight);
		};
		img.onerror = (e) => {
			console.error("[MARKER ERROR]", img.src, e);
		};

		img.onload = () => {
			const offset = (rect.width * 0.032) / 2;
			const top = ((y - offset) / rect.height) * 100;
			const left = ((x - offset) / rect.width) * 100;

			// Define constant to determine which marker collection to write to
			const activeCollection = this.mapId === "myMap" ? app.state.damageMarkers : app.state.interiorDamageMarkers;

			// Generate key for appropriate collection
			const markerKey = `marker${Object.keys(activeCollection).length}`;

			// Build payload once for marker storage
			const markerData = {
				type: this.type,
				width: img.width,
				height: img.height,
				insertionTop: top,
				insertionLeft: left,
				src,
				alt,
				x,
				y,
			};

			this.addMarkerToCollection(activeCollection, markerKey, markerData);

			this.addMarkerToDOM(img.width, img.height, top, left, src, alt);

			if (activeCollection === app.state.damageMarkers) {
				storeDamageMarkers(app.state.damageMarkers);
			} else {
				storeInteriorDamageMarkers(app.state.interiorDamageMarkers);
			}

			this.updateRemoveButtonState();
		};
	}

	addMarkerToCollection(collection, markerKey, markerData) {
		collection[markerKey] = markerData;
	}

	addMarkerToDOM(w, h, top, left, src, alt) {
		const container = document.getElementById(this.mapId);
		if (!container) return;

		const marker = new Image();
		marker.src = mkAbs(src); // normalize to root-absolute
		marker.alt = alt;
		marker.style.position = "absolute";
		marker.classList.add("map-icon");

		marker.onload = () => {
			marker.style.top = `${top}%`;
			marker.style.left = `${left}%`;
		};

		container.appendChild(marker);

		// Enable remove button if this is the exterior map
		if (this.removeBtn) this.removeBtn.removeAttribute("disabled");
	}

	resetState() {
		this.x = null;
		this.y = null;
		this.rect = null;
	}

	restoreMarkers() {
		const markers = app.state.damageMarkers;
		for (const key in markers) {
			const m = markers[key];
			if (m.type !== this.type) continue;
			this.addMarkerToDOM(m.width, m.height, m.insertionTop, m.insertionLeft, mkAbs(m.src), m.alt);
		}
	}

	removeLastMarker() {
		const container = document.getElementById(this.mapId);
		if (!container || container.children.length <= 1) return;

		// Remove last DOM marker
		const last = container.lastChild;
		container.removeChild(last);

		// Choose the active in-memory collection (exterior vs interior)
		const activeCollection = this.mapId === "myMap" ? app.state.damageMarkers : app.state.interiorDamageMarkers;

		// Remove last entry of this.type from the active collection
		const keys = Object.keys(activeCollection);
		for (let i = keys.length - 1; i >= 0; i--) {
			const key = keys[i];
			if (activeCollection[key].type === this.type) {
				delete activeCollection[key];
				break;
			}
		}

		// Disable remove button if only base image remains (keeping your existing behavior)
		if (container.children.length === 1 && this.removeBtn) {
			this.removeBtn.setAttribute("disabled", "");
		}

		// Persist the same collection we just modified
		if (this.mapId === "myMap") {
			storeDamageMarkers(activeCollection);
		} else {
			storeInteriorDamageMarkers(activeCollection);
		}

		this.updateRemoveButtonState();
	}

	getMarkerImage(type) {
		const mapping = {
			scrape: [damageMarkerIcons.exclamationTriangle, "Scrape damage icon"],
			scratch: [damageMarkerIcons.timesCircle, "Scratch damage icon"],
			dent: [damageMarkerIcons.crosshairs, "Dent damage icon"],
			broken: [damageMarkerIcons.exclamationCircle, "Broken damage icon"],
			missing: [damageMarkerIcons.questionCircle, "Missing damage icon"],
			stained: [damageMarkerIcons.stainedCircle, "Stained damage icon"],
		};
		const [src, alt] = mapping[type] || [];
		if (!src) return {};

		return { src, alt };
	}

	updateRemoveButtonState() {
		if (!this.removeBtn) return;

		// Pick the correct collection based on the current map
		const activeCollection = this.mapId === "myMap" ? app.state.damageMarkers : app.state.interiorDamageMarkers;

		const hasTypeMarkers = Object.values(activeCollection || {}).some((m) => m.type === this.type);

		if (hasTypeMarkers) {
			this.removeBtn.removeAttribute("disabled");
		} else {
			this.removeBtn.setAttribute("disabled", "");
		}
	}
}
